import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Overline } from "@/components/motion";
import { getDestinations } from "@/lib/api";

const MotionLink = motion(Link);

const LAYOUT = [
  "md:col-span-5 md:row-span-2",
  "md:col-span-4",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-4",
  "md:col-span-5 md:row-span-2",
  "md:col-span-4",
  "md:col-span-3",
];

export default function TopDestinations({ title, subtitle }) {
  const [dest, setDest] = useState([]);
  useEffect(() => {
    getDestinations({ featured: true }).then((d) => setDest(d.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <section id="destinations" data-testid="home-destinations" className="relative py-24 sm:py-32 bg-[#faf8f5]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <Overline>{title || "Top Destinations"}</Overline>
            <h2 className="font-serif text-[#3b1a1a] text-4xl sm:text-6xl leading-[1.03] tracking-tight mt-5">
              Where will India <span className="italic text-[#14523d]">take you?</span>
            </h2>
          </div>
          <Link to="/destinations" className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[#7c1d1d] hover:gap-3 transition-all">
            All Destinations <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 auto-rows-[190px] sm:auto-rows-[220px] gap-4 sm:gap-5">
          {dest.map((d, i) => (
            <MotionLink
              key={d.id}
              to={`/destinations/${d.slug}`}
              data-testid={`destination-card-${d.slug}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.05 }}
              className={`relative overflow-hidden group border border-[#d4af37]/30 hover:border-[#d4af37]/70 transition-all col-span-2 sm:col-span-1 ${LAYOUT[i] || "md:col-span-3"}`}
            >
              <img src={d.banner_image} alt={d.name} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1400ms]" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2d1616]/85 via-[#2d1616]/20 to-transparent" />
              <div className="absolute top-3 right-3 w-9 h-9 border-t border-r border-[#d4af37]" />
              <div className="absolute bottom-3 left-3 w-9 h-9 border-b border-l border-[#d4af37]" />
              <div className="absolute inset-0 flex flex-col justify-end p-5 sm:p-6 text-white">
                {d.region && <div className="text-[10px] tracking-[0.26em] uppercase text-[#e8c970] mb-1.5">{d.region}</div>}
                <div className="font-serif text-3xl sm:text-4xl leading-none">{d.name}</div>
                <div className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  Explore <span className="w-6 h-px bg-[#d4af37]" />
                </div>
              </div>
            </MotionLink>
          ))}
        </div>
      </div>
    </section>
  );
}
